import { i18n } from '@/locale/i18n'
import { i18n_en_us } from '@/locale/i18n_en_us'

/**
 * Codes with a human message under `api_errors.codes.*`. Derived from the English
 * dictionary so an unmapped code falls through quietly instead of tripping the
 * i18n missing-key warning on every API failure.
 */
const MAPPED_CODES = new Set(Object.keys(i18n_en_us.api_errors.codes))

/**
 * Normalizes the error shapes the API layer can produce into { code, detail, attr }.
 * Handles both `err.errors[0]` and the nested `err.error.errors[0]` wrapper.
 */
export const getErrorInfo = error => {
  const first = error?.error?.errors?.[0] || error?.errors?.[0]
  if (!first) return null
  return { code: first.code, detail: first.detail, attr: first.attr }
}

/**
 * Backend codes arrive as snake_case or PascalCase identifiers. A `detail` that
 * matches the same shape is a code echoed into the message field, not prose.
 */
const isMachineString = value =>
  typeof value === 'string' && value.length > 0 && !/\s/.test(value) && /^[A-Za-z][A-Za-z0-9_.-]*$/.test(value)

const translateCode = code => (code && MAPPED_CODES.has(code) ? i18n(`api_errors.codes.${code}`) : null)

const NETWORK_REASON = /failed to fetch|networkerror|network request failed|load failed/i

/**
 * `requestService.callRequest` catches a rejected fetch and rethrows a plain object,
 * keeping the original rejection on `reason` / `reason_name` (services/request.js).
 * That branch runs only when fetch rejected with no HTTP response, so the presence
 * of those fields is the connectivity signal — the raw TypeError never reaches here.
 * An aborted request is a deliberate cancellation, not a connectivity problem.
 */
const isNetworkError = error => {
  if (!error) return false
  if (error.reason_name || error.reason) {
    return error.reason_name !== 'AbortError'
  }
  return (
    error instanceof TypeError ||
    error.name === 'NetworkError' ||
    NETWORK_REASON.test(error.message || '')
  )
}

/**
 * Turns an API error into a sentence a person can act on.
 * Order: known code → human `detail` from the backend → generic fallback.
 * A `detail` that is itself a code is never surfaced.
 */
export const resolveErrorMessage = (error, fallbackKey = 'api_errors.generic.body') => {
  if (isNetworkError(error)) return i18n('api_errors.network.body')

  const info = getErrorInfo(error)

  const fromCode = translateCode(info?.code)
  if (fromCode) return fromCode

  if (info?.detail && !isMachineString(info.detail)) return info.detail

  const fromDetailAsCode = translateCode(info?.detail)
  if (fromDetailAsCode) return fromDetailAsCode

  return i18n(fallbackKey)
}

/**
 * Builds the { title, body } pair for a failure toast.
 * @param {Error|object} error - the caught API error
 * @param {string} titleKey - i18n key naming the action that failed
 */
export const buildErrorToast = (error, titleKey = 'api_errors.generic.title') => ({
  title: isNetworkError(error) ? i18n('api_errors.network.title') : i18n(titleKey),
  body: resolveErrorMessage(error),
})
